import { useEffect, useState } from "react";
import { ExpandAltOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import { HiOutlinePencil, HiOutlinePlus, HiOutlineTrash } from "react-icons/hi";
import PageContainer from "../../../components/page-container";
import Button from "../../../components/button/button";
import Message from "../../../components/toast-message";
import { useAgentConfigSource } from "../../../helpers/hooks/agent-config-source";
import { buildAgentNavigation } from "../../../consts/agent-nav-config";
import SubAgentMissing from "../sub-agent-scope/sub-agent-missing";
import { ToolModalWithForm, emptyForm, paramsMapToArray } from "../agent-tools";

const TOOL_TYPES = [
	{ value: "function", label: "Function" },
	{ value: "json", label: "JSON" },
	{ value: "kb", label: "Knowledge Base" },
	{ value: "workflow", label: "Workflow" },
];

const PERSONALITY_OPTIONS = [
	{ value: "friendly", label: "Friendly" },
	{ value: "calm", label: "Calm" },
	{ value: "approachable", label: "Approachable" },
	{ value: "expert_customer_service", label: "Expert Customer Service" },
	{ value: "professional", label: "Professional" },
	{ value: "empathetic", label: "Empathetic" },
];

const TONE_OPTIONS = [
	{ value: "warm", label: "Warm" },
	{ value: "concise", label: "Concise" },
	{ value: "confident", label: "Confident" },
	{ value: "never_fawning", label: "Never Fawning" },
	{ value: "formal", label: "Formal" },
	{ value: "casual", label: "Casual" },
];

function OptionSelector({ label, options, selected, customActive, customValue, onToggle, onCustomToggle, onCustomChange }) {
	return (
		<div className="border border-gray-300 rounded-lg bg-white p-4">
			<span className="font-semibold text-sm text-gray-800">{label}</span>
			<div className="flex flex-wrap gap-2 mt-3">
				{options.map((opt) => {
					const isActive = selected.includes(opt.value);
					return (
						<button
							key={opt.value}
							type="button"
							onClick={() => onToggle(opt.value)}
							className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
								isActive
									? "bg-primary text-white border-primary"
									: "bg-gray-100 text-gray-600 border-gray-200 hover:border-primary hover:text-primary"
							}`}
						>
							{opt.label}
						</button>
					);
				})}
				<button
					type="button"
					onClick={onCustomToggle}
					className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
						customActive
							? "bg-primary text-white border-primary"
							: "bg-gray-100 text-gray-600 border-gray-200 hover:border-primary hover:text-primary"
					}`}
				>
					Custom
				</button>
			</div>
			{customActive && (
				<textarea
					value={customValue}
					onChange={(e) => onCustomChange(e.target.value)}
					placeholder={`Describe your custom ${label.toLowerCase()}…`}
					rows={3}
					className="w-full mt-3 resize-none border border-gray-200 rounded-md outline-none focus:ring-1 focus:ring-primary focus:border-primary text-sm text-gray-800 placeholder-gray-400 p-2"
				/>
			)}
		</div>
	);
}

function PromptTextArea({
	value,
	onChange,
	minRows = 12,
	disabled,
	placeholder = "You are a helpful assistant...",
}) {
	return (
		<textarea
			value={value}
			onChange={(e) => onChange(e.target.value)}
			placeholder={placeholder}
			rows={minRows}
			disabled={disabled}
			className="w-full resize-none border-none outline-none focus:ring-0 focus:border-transparent text-sm text-gray-800 placeholder-gray-400 disabled:opacity-50"
		/>
	);
}

function ToolCard({ tool, onEdit, onDelete }) {
	const paramCount = Object.keys(tool.parameters || {}).length;
	const typeLabel = TOOL_TYPES.find((t) => t.value === tool.type)?.label || tool.type || "";

	return (
		<div className="flex flex-row items-start justify-between border border-gray-200 rounded-lg bg-white px-4 py-3 hover:shadow-sm transition-shadow">
			<div className="flex-1 min-w-0 pr-4">
				<div className="flex items-center gap-2">
					<p className="text-sm font-semibold text-gray-800 font-mono truncate">{tool.name}</p>
					{typeLabel && (
						<span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
							{typeLabel}
						</span>
					)}
				</div>
				{tool.description && (
					<p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{tool.description}</p>
				)}
				<p className="text-xs text-gray-400 mt-1">
					{paramCount} parameter{paramCount !== 1 ? "s" : ""}
				</p>
			</div>
			<div className="flex items-center gap-2 flex-shrink-0">
				<button
					type="button"
					onClick={() => onEdit(tool)}
					className="p-1.5 text-gray-500 hover:text-primary transition-colors rounded"
					title="Edit tool"
				>
					<HiOutlinePencil className="h-4 w-4" />
				</button>
				<button
					type="button"
					onClick={() => onDelete(tool)}
					className="p-1.5 text-gray-500 hover:text-red-500 transition-colors rounded"
					title="Delete tool"
				>
					<HiOutlineTrash className="h-4 w-4" />
				</button>
			</div>
		</div>
	);
}

export default function AgentPromptBased() {
	const {
		appId,
		subAgentId,
		subAgent,
		isSubAgent,
		scopeLoading,
		scopeError,
		instructions: instructionsSource,
		tools: toolsSource,
		saveMeta,
	} = useAgentConfigSource();

	const [prompt, setPrompt] = useState("");
	const [description, setDescription] = useState("");
	const [personality, setPersonality] = useState([]);
	const [personalityCustomActive, setPersonalityCustomActive] = useState(false);
	const [personalityCustom, setPersonalityCustom] = useState("");
	const [tone, setTone] = useState([]);
	const [toneCustomActive, setToneCustomActive] = useState(false);
	const [toneCustom, setToneCustom] = useState("");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);

	// Track whether a record already exists (to know POST vs PUT)
	const [isExisting, setIsExisting] = useState(false);

	const [loadState, setLoadState] = useState({ processing: false, failed: false });
	const [saveState, setSaveState] = useState({ processing: false, success: false, failed: false });

	// tools (tools) state
	const [tools, setTools] = useState([]);
	const [toolsLoading, setToolsLoading] = useState(false);
	const [toolModalOpen, setToolModalOpen] = useState(false);
	const [editingTool, setEditingTool] = useState(null);
	const [editingForm, setEditingForm] = useState(emptyForm());
	const [deletingTool, setDeletingTool] = useState(null);
	const [deleteProcessing, setDeleteProcessing] = useState(false);

	// --- Load prompt ---
	async function fetchInstructions() {
		setLoadState({ processing: true, failed: false });
		const data = await instructionsSource.get();
		if (data) {
			setPrompt(data.globalPrompt || "");
			// Restore personality
			const pRaw = data.personality || "";
			if (pRaw) {
				const pItems = pRaw.split(",").map((s) => s.trim()).filter(Boolean);
				const predefined = pItems.filter((v) => PERSONALITY_OPTIONS.some((o) => o.value === v));
				const custom = pItems.filter((v) => !PERSONALITY_OPTIONS.some((o) => o.value === v));
				setPersonality(predefined);
				if (custom.length > 0) {
					setPersonalityCustomActive(true);
					setPersonalityCustom(custom.join(", "));
				}
			}
			// Restore tone
			const tRaw = data.tone || "";
			if (tRaw) {
				const tItems = tRaw.split(",").map((s) => s.trim()).filter(Boolean);
				const predefined = tItems.filter((v) => TONE_OPTIONS.some((o) => o.value === v));
				const custom = tItems.filter((v) => !TONE_OPTIONS.some((o) => o.value === v));
				setTone(predefined);
				if (custom.length > 0) {
					setToneCustomActive(true);
					setToneCustom(custom.join(", "));
				}
			}
			setIsExisting(true);
		}
		setLoadState({ processing: false, failed: false });
	}

	// --- Load tools ---
	async function fetchTools() {
		setToolsLoading(true);
		setTools(await toolsSource.list());
		setToolsLoading(false);
	}

	useEffect(() => {
		// in sub-agent scope the record must land before its toolData can be read
		if (appId && !scopeLoading) {
			fetchInstructions();
			fetchTools();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [appId, subAgentId, scopeLoading]);

	// description lives on the sub-agent record itself, not in toolData
	useEffect(() => {
		setDescription(subAgent?.description || "");
	}, [subAgent]);

	// --- Clear ---
	const handleClear = () => {
		setPrompt("");
		setDescription(subAgent?.description || "");
		setPersonality([]);
		setPersonalityCustomActive(false);
		setPersonalityCustom("");
		setTone([]);
		setToneCustomActive(false);
		setToneCustom("");
		setSaveState({ processing: false, success: false, failed: false });
	};

	// --- Save (POST or PUT) ---
	const handleSave = async () => {
		setSaveState({ processing: true, success: false, failed: false });
		const personalityParts = [...personality, ...(personalityCustomActive && personalityCustom ? [personalityCustom] : [])];
		const toneParts = [...tone, ...(toneCustomActive && toneCustom ? [toneCustom] : [])];
		const payload = {
			globalPrompt: prompt,
			personality: personalityParts.join(", "),
			tone: toneParts.join(", "),
		};
		try {
			const { status } = await instructionsSource.save(payload, isExisting);
			const promptOk = status === 200 || status === 201;

			// The description is a field of the sub-agent record rather than part
			// of the prompt config, so it needs its own write — skipped when the
			// text has not changed.
			let metaOk = true;
			if (isSubAgent && description !== (subAgent?.description || "")) {
				const res = await saveMeta({ description });
				metaOk = res.ok;
			}

			if (promptOk && metaOk) {
				setIsExisting(true);
				setSaveState({ processing: false, success: true, failed: false });
			} else {
				setSaveState({ processing: false, success: false, failed: true });
			}
		} catch {
			setSaveState({ processing: false, success: false, failed: true });
		}
	};

	// --- tool modal handlers ---
	const handleAddTool = () => {
		setEditingTool(null);
		setEditingForm(emptyForm());
		setToolModalOpen(true);
	};

	const handleEditTool = (tool) => {
		setEditingTool(tool);
		setEditingForm({
			name: tool.name || "",
			type: tool.type || "",
			description: tool.description || "",
			parameters: paramsMapToArray(tool.parameters),
			toolData: tool.toolData || {},
		});
		setToolModalOpen(true);
	};

	const handleToolModalClose = () => {
		setToolModalOpen(false);
		setEditingTool(null);
	};

	const handleToolSaved = () => {
		setToolModalOpen(false);
		setEditingTool(null);
		fetchTools();
	};

	const handleDeleteClick = (tool) => setDeletingTool(tool);

	const handleDeleteConfirm = async () => {
		if (!deletingTool) return;
		setDeleteProcessing(true);
		await toolsSource.remove(deletingTool.id);
		setDeleteProcessing(false);
		setDeletingTool(null);
		fetchTools();
	};

	const isBusy = loadState.processing || saveState.processing;
	const navigation = buildAgentNavigation({ subAgentId });

	if (isSubAgent && !scopeLoading && scopeError) {
		return <SubAgentMissing navigation={navigation} />;
	}

	return (
		<PageContainer
			title={isSubAgent ? subAgent?.name || "Sub-Agent" : "Agent"}
			subtitle={
				isSubAgent
					? "Instruct this sub-agent and let the main agent delegate to it"
					: "Instruct your agent with a prompt and let it handle the rest"
			}
			navigationPosition="below-header"
			navigation={navigation}
			headerAction={
				<div className="flex flex-row items-center gap-3">
					<Button text="Clear" color="default" onClick={handleClear} disabled={isBusy} />
					<Button text="Save" onClick={handleSave} loading={saveState.processing} disabled={isBusy} />
				</div>
			}
		>
			{saveState.success && (
				<div className="mb-4">
					<Message text="Settings saved successfully." type="success" />
				</div>
			)}
			{saveState.failed && (
				<div className="mb-4">
					<Message text="Failed to save settings. Please try again." type="failure" />
				</div>
			)}

			{loadState.processing || scopeLoading ? (
				<div className="flex justify-center items-center py-16">
					<p className="text-sm text-gray-400">Loading…</p>
				</div>
			) : (
				<div className="flex flex-row gap-6 mt-6 px-1">
					{/* Left Panel: Prompt Editor */}
					<div className="flex-1">
						{/* Description is what the main agent reads when deciding
						    whether to delegate, so a sub-agent edits it here. */}
						{isSubAgent && (
							<div className="border border-gray-300 rounded-lg bg-white p-4 mb-4">
								<div className="flex justify-between items-center mb-2">
									<span className="font-semibold text-sm text-gray-800">Description</span>
									<button
										type="button"
										onClick={() => setIsDescriptionModalOpen(true)}
										title="Expand Editor"
										className="text-gray-500 hover:text-primary transition-colors p-1 rounded"
									>
										<ExpandAltOutlined />
									</button>
								</div>
								<PromptTextArea
									value={description}
									onChange={setDescription}
									minRows={4}
									placeholder="Delegates research tasks to a specialized agent..."
								/>
							</div>
						)}

						<div className="border border-gray-300 rounded-lg bg-white p-4">
							<div className="flex justify-between items-center mb-2">
								<span className="font-semibold text-sm text-gray-800">System prompt</span>
								<button
									type="button"
									onClick={() => setIsModalOpen(true)}
									title="Expand Editor"
									className="text-gray-500 hover:text-primary transition-colors p-1 rounded"
								>
									<ExpandAltOutlined />
								</button>
							</div>
							<PromptTextArea value={prompt} onChange={setPrompt} />
						</div>

						<div className={`mt-4 ${isSubAgent ? "hidden" : ""}`}>
							<OptionSelector
								label="Personality"
								options={PERSONALITY_OPTIONS}
								selected={personality}
								customActive={personalityCustomActive}
								customValue={personalityCustom}
								onToggle={(val) =>
									setPersonality((prev) => {
										const next = prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val];
										if (next.length > 0) {
											setPersonalityCustomActive(false);
											setPersonalityCustom("");
										}
										return next;
									})
								}
								onCustomToggle={() => {
									if (!personalityCustomActive) {
										setPersonality([]);
									}
									setPersonalityCustomActive((prev) => !prev);
									if (personalityCustomActive) setPersonalityCustom("");
								}}
								onCustomChange={setPersonalityCustom}
							/>
						</div>

						<div className={`mt-4 ${isSubAgent ? "hidden" : ""}`}>
							<OptionSelector
								label="Tone"
								options={TONE_OPTIONS}
								selected={tone}
								customActive={toneCustomActive}
								customValue={toneCustom}
								onToggle={(val) =>
									setTone((prev) => {
										const next = prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val];
										if (next.length > 0) {
											setToneCustomActive(false);
											setToneCustom("");
										}
										return next;
									})
								}
								onCustomToggle={() => {
									if (!toneCustomActive) {
										setTone([]);
									}
									setToneCustomActive((prev) => !prev);
									if (toneCustomActive) setToneCustom("");
								}}
								onCustomChange={setToneCustom}
							/>
						</div>
					</div>

					{/* Right Panel: tools List */}
					<div className="w-64 flex flex-col">
						<div className="flex justify-between items-center mb-3">
							<span className="font-semibold text-sm text-gray-800">Tools</span>
							<button
								type="button"
								onClick={handleAddTool}
								className="flex items-center gap-1 text-sm text-primary hover:text-secondary font-medium transition-colors"
							>
								<HiOutlinePlus className="h-4 w-4" />
								Add
							</button>
						</div>

						{toolsLoading ? (
							<div className="flex justify-center items-center py-8">
								<p className="text-sm text-gray-400">Loading tools…</p>
							</div>
						) : tools.length === 0 ? (
							<div className="flex flex-col items-center justify-center py-8 text-center border border-dashed border-gray-300 rounded-lg">
								<p className="text-gray-500 text-sm font-medium">No tools yet</p>
								<p className="text-gray-400 text-xs mt-1 mb-3">
									Add a tool to agent.
								</p>
								<Button text="Add tool" onClick={handleAddTool} />
							</div>
						) : (
							<div className="flex flex-col gap-2">
								{tools.map((tool) => (
									<ToolCard
										key={tool.id}
										tool={tool}
										onEdit={handleEditTool}
										onDelete={handleDeleteClick}
									/>
								))}
							</div>
						)}
					</div>
				</div>
			)}

			{/* Expanded Prompt Modal */}
			<Modal
				title={<span className="font-semibold text-gray-800">System prompt</span>}
				open={isModalOpen}
				onOk={() => setIsModalOpen(false)}
				onCancel={() => setIsModalOpen(false)}
				width={800}
				footer={
					<div className="flex justify-end">
						<Button text="Done" onClick={() => setIsModalOpen(false)} />
					</div>
				}
			>
				<div className="mt-3">
					<PromptTextArea value={prompt} onChange={setPrompt} minRows={20} />
				</div>
			</Modal>

			<Modal
				title={<span className="font-semibold text-gray-800">Description</span>}
				open={isDescriptionModalOpen}
				onOk={() => setIsDescriptionModalOpen(false)}
				onCancel={() => setIsDescriptionModalOpen(false)}
				width={800}
				footer={
					<div className="flex justify-end">
						<Button text="Done" onClick={() => setIsDescriptionModalOpen(false)} />
					</div>
				}
			>
				<div className="mt-3">
					<PromptTextArea
						value={description}
						onChange={setDescription}
						minRows={20}
						placeholder="Delegates research tasks to a specialized agent..."
					/>
				</div>
			</Modal>

			{/* Add / Edit tool modal */}
			<ToolModalWithForm
				open={toolModalOpen}
				toolId={editingTool?.id}
				initialForm={editingForm}
				onClose={handleToolModalClose}
				onSaved={handleToolSaved}
				appId={appId}
				onSave={isSubAgent ? toolsSource.save : undefined}
			/>
			{/* Delete tool confirmation */}
			<Modal
				title={<span className="font-semibold text-gray-800">Delete tool</span>}
				open={!!deletingTool}
				onCancel={() => setDeletingTool(null)}
				footer={
					<div className="flex justify-end gap-2">
						<Button text="Cancel" color="default" onClick={() => setDeletingTool(null)} disabled={deleteProcessing} />
						<Button text="Delete" color="error" onClick={handleDeleteConfirm} loading={deleteProcessing} />
					</div>
				}
			>
				<p className="text-sm text-gray-600 mt-2">
					Are you sure you want to delete <span className="font-mono font-semibold">{deletingTool?.name}</span>? This
					action cannot be undone.
				</p>
			</Modal>
		</PageContainer>
	);
}
